import React, { useState, useRef, useEffect, useCallback } from 'react';
import {
  format,
  startOfDay,
  subDays,
  startOfMonth,
  eachDayOfInterval,
  isSameDay,
  isWithinInterval,
  addMonths,
  subMonths,
  getDay,
  endOfMonth,
} from 'date-fns';
import { Calendar, ChevronLeft, ChevronRight } from 'lucide-react';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface AdminDateRangePickerProps {
  startDate: Date;
  endDate: Date;
  onChange: (start: Date, end: Date) => void;
}

interface Preset {
  label: string;
  days: number; // 0 = today
}

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const PRESETS: Preset[] = [
  { label: 'Today', days: 0 },
  { label: 'Last 7 Days', days: 6 },
  { label: 'Last 30 Days', days: 29 },
  { label: 'Last 90 Days', days: 89 },
  { label: 'Last Year', days: 364 },
];

const WEEKDAY_HEADERS = ['Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa', 'Su'];

// ---------------------------------------------------------------------------
// Styles
// ---------------------------------------------------------------------------

const colors = {
  bg: '#0f172a',
  bgElevated: '#1e293b',
  bgHover: '#16213e',
  border: '#334155',
  text: '#e2e8f0',
  textMuted: '#94a3b8',
  textDim: '#64748b',
  accent: '#60a5fa',
  accentDark: '#2563eb',
  accentSubtle: 'rgba(96, 165, 250, 0.15)',
} as const;

const styles: Record<string, React.CSSProperties> = {
  // Trigger button
  trigger: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 8,
    padding: '8px 14px',
    background: colors.bgElevated,
    border: `1px solid ${colors.border}`,
    borderRadius: 8,
    color: colors.text,
    fontSize: 14,
    fontWeight: 500,
    cursor: 'pointer',
    transition: 'border-color 0.15s',
    userSelect: 'none',
  },

  // Popover wrapper
  popoverWrapper: {
    position: 'relative',
    display: 'inline-block',
    zIndex: 9999,
  },
  popover: {
    position: 'absolute',
    top: 'calc(100% + 6px)',
    left: 0,
    zIndex: 9999,
    display: 'flex',
    background: colors.bgElevated,
    border: `1px solid ${colors.border}`,
    borderRadius: 12,
    boxShadow: '0 16px 48px rgba(0,0,0,0.45)',
    isolation: 'isolate',
  },

  // Sidebar
  sidebar: {
    display: 'flex',
    flexDirection: 'column',
    gap: 2,
    padding: '16px 12px',
    borderRight: `1px solid ${colors.border}`,
    minWidth: 150,
  },
  presetBtn: {
    padding: '8px 12px',
    background: 'transparent',
    border: 'none',
    borderRadius: 6,
    color: colors.textMuted,
    fontSize: 13,
    fontWeight: 500,
    textAlign: 'left' as const,
    cursor: 'pointer',
    transition: 'background 0.12s, color 0.12s',
  },

  // Calendar area
  calendarArea: {
    display: 'flex',
    flexDirection: 'column',
    padding: 16,
  },
  calendarsRow: {
    display: 'flex',
    gap: 24,
  },

  // Single month
  monthContainer: {
    width: 260,
  },
  monthHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
    marginBottom: 12,
  },
  monthTitle: {
    fontSize: 14,
    fontWeight: 600,
    color: colors.text,
    minWidth: 140,
    textAlign: 'center' as const,
  },
  navBtn: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: 28,
    height: 28,
    background: 'transparent',
    border: 'none',
    borderRadius: 6,
    color: colors.textMuted,
    cursor: 'pointer',
    transition: 'background 0.12s',
  },
  weekdayRow: {
    display: 'grid',
    gridTemplateColumns: 'repeat(7, 1fr)',
    marginBottom: 4,
  },
  weekdayCell: {
    fontSize: 11,
    fontWeight: 600,
    color: colors.textDim,
    textAlign: 'center' as const,
    padding: '4px 0',
    textTransform: 'uppercase' as const,
    letterSpacing: '0.05em',
  },
  dayGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(7, 1fr)',
    gap: 1,
  },
  dayCell: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: 34,
    height: 32,
    margin: '0 auto',
    fontSize: 13,
    fontWeight: 500,
    borderRadius: 6,
    border: 'none',
    cursor: 'pointer',
    transition: 'background 0.1s, color 0.1s',
  },

  // Footer
  footer: {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: 8,
    padding: '12px 16px 16px',
    borderTop: `1px solid ${colors.border}`,
  },
  cancelBtn: {
    padding: '7px 16px',
    background: 'transparent',
    border: `1px solid ${colors.border}`,
    borderRadius: 6,
    color: colors.textMuted,
    fontSize: 13,
    fontWeight: 500,
    cursor: 'pointer',
  },
  applyBtn: {
    padding: '7px 20px',
    background: colors.accentDark,
    border: 'none',
    borderRadius: 6,
    color: '#fff',
    fontSize: 13,
    fontWeight: 600,
    cursor: 'pointer',
    transition: 'background 0.12s',
  },
};

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/** Monday-based day index (0 = Mon, 6 = Sun) */
function mondayIndex(date: Date): number {
  return (getDay(date) + 6) % 7;
}

function buildCalendarDays(month: Date): (Date | null)[] {
  const start = startOfMonth(month);
  const end = endOfMonth(month);
  const days = eachDayOfInterval({ start, end });
  const blanks: null[] = new Array(mondayIndex(start)).fill(null);
  return [...blanks, ...days];
}

// ---------------------------------------------------------------------------
// MonthCalendar sub-component
// ---------------------------------------------------------------------------

interface MonthCalendarProps {
  month: Date;
  selectionStart: Date | null;
  selectionEnd: Date | null;
  onDayClick: (day: Date) => void;
}

function MonthCalendar({ month, selectionStart, selectionEnd, onDayClick }: MonthCalendarProps) {
  const cells = buildCalendarDays(month);
  const today = startOfDay(new Date());

  function dayStyle(day: Date): React.CSSProperties {
    const base: React.CSSProperties = {
      ...styles.dayCell,
      background: 'transparent',
      color: colors.text,
    };

    const isStart = selectionStart && isSameDay(day, selectionStart);
    const isEnd = selectionEnd && isSameDay(day, selectionEnd);
    const inRange =
      selectionStart &&
      selectionEnd &&
      isWithinInterval(day, {
        start: selectionStart < selectionEnd ? selectionStart : selectionEnd,
        end: selectionStart < selectionEnd ? selectionEnd : selectionStart,
      });

    if (isStart || isEnd) {
      return { ...base, background: colors.accentDark, color: '#fff', fontWeight: 700 };
    }
    if (inRange) {
      return { ...base, background: colors.accentSubtle, color: colors.accent };
    }
    if (isSameDay(day, today)) {
      return { ...base, color: colors.accent, fontWeight: 700 };
    }
    return base;
  }

  return (
    <div style={styles.monthContainer}>
      <div style={styles.weekdayRow}>
        {WEEKDAY_HEADERS.map((d) => (
          <div key={d} style={styles.weekdayCell}>{d}</div>
        ))}
      </div>
      <div style={styles.dayGrid}>
        {cells.map((cell, i) =>
          cell ? (
            <button
              key={i}
              style={dayStyle(cell)}
              onMouseEnter={(e) => {
                if (
                  !(selectionStart && isSameDay(cell, selectionStart)) &&
                  !(selectionEnd && isSameDay(cell, selectionEnd))
                ) {
                  (e.currentTarget as HTMLButtonElement).style.background = colors.bgHover;
                }
              }}
              onMouseLeave={(e) => {
                // Re-derive correct background on leave
                const s = dayStyle(cell);
                (e.currentTarget as HTMLButtonElement).style.background = s.background as string;
              }}
              onClick={() => onDayClick(cell)}
            >
              {format(cell, 'd')}
            </button>
          ) : (
            <div key={`blank-${i}`} style={{ width: 34, height: 32 }} />
          )
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// AdminDateRangePicker
// ---------------------------------------------------------------------------

export function AdminDateRangePicker({ startDate, endDate, onChange }: AdminDateRangePickerProps) {
  const [open, setOpen] = useState(false);
  const [leftMonth, setLeftMonth] = useState(() => startOfMonth(startDate));
  const [pickStart, setPickStart] = useState<Date | null>(null);
  const [pickEnd, setPickEnd] = useState<Date | null>(null);
  const popoverRef = useRef<HTMLDivElement>(null);

  const rightMonth = addMonths(leftMonth, 1);

  // Selection state: while picking, use local state; otherwise use props
  const displayStart = pickStart ?? startDate;
  const displayEnd = pickEnd ?? (pickStart ? null : endDate);

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    function handleClick(e: MouseEvent) {
      if (popoverRef.current && !popoverRef.current.contains(e.target as Node)) {
        setOpen(false);
        setPickStart(null);
        setPickEnd(null);
      }
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [open]);

  // Close on ESC
  useEffect(() => {
    if (!open) return;
    function handleKey(e: KeyboardEvent) {
      if (e.key === 'Escape') {
        setOpen(false);
        setPickStart(null);
        setPickEnd(null);
      }
    }
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [open]);

  // Sync leftMonth when props change (e.g. external reset)
  useEffect(() => {
    setLeftMonth(startOfMonth(startDate));
  }, [startDate]);

  const handlePreset = useCallback(
    (days: number) => {
      const today = startOfDay(new Date());
      const start = days === 0 ? today : subDays(today, days);
      onChange(start, today);
      setPickStart(null);
      setPickEnd(null);
      setOpen(false);
    },
    [onChange]
  );

  const handleDayClick = useCallback(
    (day: Date) => {
      if (!pickStart || pickEnd) {
        // First click (or restart after both set)
        setPickStart(day);
        setPickEnd(null);
      } else {
        // Second click — set end
        if (day < pickStart) {
          setPickStart(day);
          setPickEnd(pickStart);
        } else {
          setPickEnd(day);
        }
      }
    },
    [pickStart, pickEnd]
  );

  const handleApply = useCallback(() => {
    if (pickStart) {
      const end = pickEnd ?? pickStart;
      const [s, e] = pickStart <= end ? [pickStart, end] : [end, pickStart];
      onChange(s, e);
    }
    setPickStart(null);
    setPickEnd(null);
    setOpen(false);
  }, [pickStart, pickEnd, onChange]);

  const handleCancel = useCallback(() => {
    setPickStart(null);
    setPickEnd(null);
    setOpen(false);
  }, []);

  // Format the trigger label
  const label = isSameDay(startDate, endDate)
    ? format(startDate, 'MMM d, yyyy')
    : startDate.getFullYear() === endDate.getFullYear()
      ? `${format(startDate, 'MMM d')} – ${format(endDate, 'MMM d, yyyy')}`
      : `${format(startDate, 'MMM d, yyyy')} – ${format(endDate, 'MMM d, yyyy')}`;

  return (
    <div style={styles.popoverWrapper} ref={popoverRef}>
      {/* Trigger */}
      <button
        style={styles.trigger}
        onClick={() => setOpen((prev) => !prev)}
        onMouseEnter={(e) => {
          (e.currentTarget as HTMLButtonElement).style.borderColor = colors.accent;
        }}
        onMouseLeave={(e) => {
          (e.currentTarget as HTMLButtonElement).style.borderColor = colors.border;
        }}
      >
        <Calendar size={15} color={colors.accent} />
        {label}
      </button>

      {/* Popover */}
      {open && (
        <div style={styles.popover}>
          {/* Sidebar presets */}
          <div style={styles.sidebar}>
            {PRESETS.map((p) => (
              <button
                key={p.label}
                style={styles.presetBtn}
                onClick={() => handlePreset(p.days)}
                onMouseEnter={(e) => {
                  (e.currentTarget as HTMLButtonElement).style.background = colors.bgHover;
                  (e.currentTarget as HTMLButtonElement).style.color = colors.text;
                }}
                onMouseLeave={(e) => {
                  (e.currentTarget as HTMLButtonElement).style.background = 'transparent';
                  (e.currentTarget as HTMLButtonElement).style.color = colors.textMuted;
                }}
              >
                {p.label}
              </button>
            ))}
          </div>

          {/* Calendars + footer */}
          <div style={styles.calendarArea}>
            {/* Month navigation + calendars */}
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 4 }}>
              <button
                style={styles.navBtn}
                onClick={() => setLeftMonth((m) => subMonths(m, 1))}
                onMouseEnter={(e) => {
                  (e.currentTarget as HTMLButtonElement).style.background = colors.bgHover;
                }}
                onMouseLeave={(e) => {
                  (e.currentTarget as HTMLButtonElement).style.background = 'transparent';
                }}
              >
                <ChevronLeft size={16} />
              </button>
              <span style={styles.monthTitle}>{format(leftMonth, 'MMMM yyyy')}</span>
              <span style={{ width: 24 }} />
              <span style={styles.monthTitle}>{format(rightMonth, 'MMMM yyyy')}</span>
              <button
                style={styles.navBtn}
                onClick={() => setLeftMonth((m) => addMonths(m, 1))}
                onMouseEnter={(e) => {
                  (e.currentTarget as HTMLButtonElement).style.background = colors.bgHover;
                }}
                onMouseLeave={(e) => {
                  (e.currentTarget as HTMLButtonElement).style.background = 'transparent';
                }}
              >
                <ChevronRight size={16} />
              </button>
            </div>

            <div style={styles.calendarsRow}>
              <MonthCalendar
                month={leftMonth}
                selectionStart={displayStart}
                selectionEnd={displayEnd}
                onDayClick={handleDayClick}
              />
              <MonthCalendar
                month={rightMonth}
                selectionStart={displayStart}
                selectionEnd={displayEnd}
                onDayClick={handleDayClick}
              />
            </div>

            {/* Footer */}
            <div style={styles.footer}>
              <button
                style={styles.cancelBtn}
                onClick={handleCancel}
                onMouseEnter={(e) => {
                  (e.currentTarget as HTMLButtonElement).style.borderColor = colors.textMuted;
                }}
                onMouseLeave={(e) => {
                  (e.currentTarget as HTMLButtonElement).style.borderColor = colors.border;
                }}
              >
                Cancel
              </button>
              <button
                style={{
                  ...styles.applyBtn,
                  opacity: pickStart ? 1 : 0.5,
                  cursor: pickStart ? 'pointer' : 'default',
                }}
                onClick={handleApply}
                disabled={!pickStart}
                onMouseEnter={(e) => {
                  if (pickStart) {
                    (e.currentTarget as HTMLButtonElement).style.background = colors.accent;
                  }
                }}
                onMouseLeave={(e) => {
                  (e.currentTarget as HTMLButtonElement).style.background = colors.accentDark;
                }}
              >
                Apply
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
